import {Box} from '@mui/material';
import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {EntityTemplatesRepository} from '../../../../../../repositories/entity-templates';
import {translations} from '../../../../../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {CarePlanActivitiesGroup} from '../../../../../../backend/interfaces/care-plan-activities-group';
import {ActivitiesGroupCard} from './activities-group-card';
import {ConfigurationsValuesIndex} from '../../../../../../types';
import {EditActivitiesGroupDialog} from '../../../../../../components/care-plan/edit-activities-group-dialog/edit-activities-group-dialog';
import {CarePlanActivitiesGroupApiRequestConfigurationsItem} from '../../../../../../backend/requests/care-plan-activities-group';
import {useSnackBar} from '../../../../../../contexts/snack-bar/context';
import {CarePlanActivitiesGroupType} from '../../../../../../backend/interfaces/care-plan-activities-group-type';
import {ViewActivityEntryDialog} from '../../../../../../components/care-plan/view-activity-type-entry-dialog/view-activity-type-entry-dialog';
import {AddActivityEntryDialog} from '../../../../../../components/care-plan/add-activity-type-entry-dialog/add-activity-type-entry-dialog';
import {EditActivityEntryDialog} from '../../../../../../components/care-plan/edit-activity-type-entry-dialog/edit-activity-type-entry-dialog';
import {DeleteActivityGroupEntryConfirmation} from '../../../../../../components/care-plan/delete-activity-group-entry-confirmation/delete-activity-group-entry-confirmation';

type TemplateEditCardProps = {
  activitiesGroup: CarePlanActivitiesGroup;
  activitiesSectorId: string;
  order: number;
  editModeActivity: boolean;
  onUpdateActivityGroup: (data: CarePlanActivitiesGroup) => void;
  onDeleteActivityGroup: (id: string) => void;
  onUpdateActivityGroupType: (data: CarePlanActivitiesGroup) => void;
  setEditModeActivity: (id?: string) => void;
};

export const TemplateEditCard = ({
  activitiesGroup,
  activitiesSectorId,
  order,
  editModeActivity,
  onUpdateActivityGroup,
  onDeleteActivityGroup,
  onUpdateActivityGroupType,
  setEditModeActivity,
}: TemplateEditCardProps) => {
  const {t} = useTranslation();
  const snackBarContext = useSnackBar();
  const [editActivityGroup, setEditActivityGroup] = useState(false);
  const [configurationsValuesIndex, setConfigurationsValuesIndex] =
    useState<ConfigurationsValuesIndex>(undefined);
  const [confirmActivityGroupDeletion, setConfirmActivityGroupDeletion] = useState<string>();
  const [selectedActivityGroupTypeEntry, setSelectedActivityGroupTypeEntry] = useState<
    CarePlanActivitiesGroupType | undefined
  >(undefined);
  const [activityEntryEditType, setActivityEntryEditType] = useState<
    'create' | 'edit' | 'view' | undefined
  >(undefined);
  const [selectedActivityGroupTypeId, setSelectedActivityGroupTypeId] = useState<
    string | undefined
  >(undefined);
  const [repetitionType, setRepetitionType] = useState<
    'activities-group' | 'activity-type' | undefined
  >();
  let {entityId, templateId}: {entityId: string; templateId: string} = useParams();

  useEffect(() => {
    if (activitiesGroup.configurations) {
      for (const item of activitiesGroup.configurations) {
        try {
          const jsonValue = JSON.parse(item.carePlanConfigurationValue);
          if (typeof jsonValue === 'object' && jsonValue.hasOwnProperty('type'))
            setRepetitionType(jsonValue.type);
        } catch (error) {
          // Ignore non-parseable JSON values
        }
      }
    }
  }, [activitiesGroup.configurations]);

  const handleUpdateActivityType = (data: CarePlanActivitiesGroup) => {
    onUpdateActivityGroupType(data);
    setSelectedActivityGroupTypeEntry(undefined);
    setSelectedActivityGroupTypeId(undefined);
  };

  const handleUpdateActivity = async (
    updatedConfigurations: CarePlanActivitiesGroupApiRequestConfigurationsItem[],
    activityGroup: CarePlanActivitiesGroup
  ) => {
    await EntityTemplatesRepository.instance
      .updateTemplateActivityGroup(entityId, templateId, activityGroup.id, {
        activitiesGroupId: activityGroup.activitiesGroupId,
        configurations: updatedConfigurations,
      })
      .then((response) => {
        // Show Toast
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(translations.activities_group_entry.snackbar.update.success),
        });

        onUpdateActivityGroup(response);
      })
      .catch((error) => {
        // Show Toast
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.activities_group_entry.snackbar.update.error),
        });
      });
  };

  return (
    <Box
      id={`activity_card_${templateId}`}
      sx={{order: order}}
      key={`activities-group-entry-container-${templateId}_${activitiesSectorId}`}
    >
      <ActivitiesGroupCard
        key={`activities-card-${activitiesSectorId}-${activitiesGroup.id}`}
        activitiesSectorId={activitiesSectorId ?? ''}
        activitiesGroupId={activitiesGroup.activitiesGroupId}
        activitiesGroupConfigurations={activitiesGroup.configurations}
        activityTypes={activitiesGroup.activityTypes}
        id={activitiesGroup.id}
        editMode={editModeActivity}
        setEditMode={setEditModeActivity}
        onEditActivityGroup={(defaultValues: ConfigurationsValuesIndex) => {
          setConfigurationsValuesIndex(defaultValues);
          setEditActivityGroup(true);
        }}
        onOpenCreateActivityGroupEntry={(activityType) => {
          setActivityEntryEditType('create');
          setSelectedActivityGroupTypeId(activityType.id);
        }}
        onOpenEditActivityGroupEntry={(activitiesGroupType) => {
          setActivityEntryEditType('edit');
          setSelectedActivityGroupTypeEntry(activitiesGroupType);
        }}
        onOpenViewActivityGroupEntry={(activitiesGroupType) => {
          setActivityEntryEditType('view');
          setSelectedActivityGroupTypeEntry(activitiesGroupType);
        }}
        onDeleteActivityGroupEntry={setConfirmActivityGroupDeletion}
      />
      {/** MODALS */}

      {/* MODAL FOR EDITING ACTIVITY GROUP */}
      {editModeActivity && editActivityGroup && (
        <EditActivitiesGroupDialog
          open={editActivityGroup}
          onClose={() => setEditActivityGroup(false)}
          activitiesSectorId={activitiesSectorId ?? ''}
          activitiesGroupId={activitiesGroup.activitiesGroupId}
          onUpdate={(values) => handleUpdateActivity(values, activitiesGroup)}
          defaultValues={configurationsValuesIndex}
        />
      )}

      {/* MODAL FOR CONFIRMING DELETE ACTIVITY GROUP ENTRY */}
      {confirmActivityGroupDeletion && (
        <DeleteActivityGroupEntryConfirmation
          open={confirmActivityGroupDeletion !== undefined}
          onClose={() => setConfirmActivityGroupDeletion(undefined)}
          activitiesSectorId={activitiesSectorId ?? ''}
          entryId={activitiesGroup.id}
          activitiesGroupName={confirmActivityGroupDeletion}
          onDelete={() => onDeleteActivityGroup(activitiesGroup.id)}
        />
      )}

      {/* MODAL FOR EDITING ACTIVITY GROUP TASK ENTRY */}
      {editModeActivity && selectedActivityGroupTypeEntry && (
        <EditActivityEntryDialog
          open={activityEntryEditType === 'edit'}
          onClose={() => setActivityEntryEditType(undefined)}
          activitiesSectorId={activitiesSectorId ?? ''}
          activityGroupEntry={activitiesGroup}
          entry={selectedActivityGroupTypeEntry}
          onUpdate={handleUpdateActivityType}
          repetitionType={repetitionType}
        />
      )}

      {/* MODAL FOR VIEWING ACTIVITY GROUP TASK ENTRY */}
      {!editModeActivity && selectedActivityGroupTypeEntry && (
        <ViewActivityEntryDialog
          open={activityEntryEditType === 'view'}
          onClose={() => setActivityEntryEditType(undefined)}
          activitiesSectorId={activitiesSectorId ?? ''}
          activityGroupEntry={activitiesGroup}
          entry={selectedActivityGroupTypeEntry}
          onUpdate={handleUpdateActivityType}
          repetitionType={repetitionType}
        />
      )}

      {/* MODAL FOR ADDING/CREATING ACTIVITY GROUP TASK ENTRY */}
      {editModeActivity && selectedActivityGroupTypeId && (
        <AddActivityEntryDialog
          open={activityEntryEditType === 'create'}
          onClose={() => setActivityEntryEditType(undefined)}
          activitiesSectorId={activitiesSectorId ?? ''}
          entry={activitiesGroup}
          activityGroupTypeId={selectedActivityGroupTypeId}
          onUpdate={handleUpdateActivityType}
          repetitionType={repetitionType}
        />
      )}
    </Box>
  );
};
